import clsx from 'clsx'
import {appSelectors} from '@appStore/reducers/app'
import Badge from 'components/atoms/Badge'
import {CustomTip} from 'components/atoms/tooltipCustom'
import {useId, useState} from 'react'
import {useSwitcher, useAppSelector} from 'shared/hooks'
import {modal} from 'shared/styles'
import {
	Button,
	type ContactsTable,
	ListSelect,
	LoadingButton,
	StackIcon
} from 'shared/ui'
import {ControlledDialog} from 'shared/ui/Dialog'

import {useSendToTeamMember} from '../../hooks'
import {api as teamApi} from '../../../team/api'
import type {Persona} from '../../../team/model'
import {notificationUtils} from '../../../notifications'
import {notifyError} from 'shared/errors/notificate'

import css from './SendToTeamMember.module.scss'

export function SendToTeamMember(
	{
		amount = 0,
		className,
		filters,
		unSelectAll
	}: ContactsTable.Types.ActionsProps & {
		className?: string
	}) {
	const switcher = useSwitcher(false)
	const [value, setValue] = useState<Persona | null>(null)
	const id = useId()

	const appProfile = useAppSelector(appSelectors.appProfile)
	const persona = useAppSelector(appSelectors.persona)
	const isProPlanAccessible = appProfile?.isProPlanAccessible
	// Current acting user (when impersonating, the member we signed in as). Exclude from recipients.
	const actingPersonaId = persona?.personaId

	const personasQuery = teamApi.usePersonasQuery(undefined, {
		skip: !switcher.isOn || !isProPlanAccessible
	})

	const [send, {isLoading}] = useSendToTeamMember()

	const isOtherMember = (member: Persona) => member.persona_id !== actingPersonaId
	const teamMembers = personasQuery.data?.documents?.filter(isOtherMember) ?? []

	function closeWithReset() {
		setValue(null)
		switcher.off()
	}

	const button = (
		<Button
			className={clsx(className, !isProPlanAccessible && css.buttonWrapper)}
			disabled={!isProPlanAccessible}
			onClick={switcher.on}
			variant='bordered-dark'
		>
			<StackIcon icon='plus'/>
			Send to team member
			{!isProPlanAccessible && <Badge label='Pro'/>}
		</Button>
	)

	return (
		<>
			{!isProPlanAccessible ? (
				<CustomTip arrow title='Upgrade to Pro Plan to send contacts to members'>
					<span className={css.tooltipAnchor}>{button}</span>
				</CustomTip>
			) : (
				button
			)}

			<ControlledDialog
				className={css.wrapper}
				onRequestClose={closeWithReset}
				open={switcher.isOn}
				rootClassName={css.root}
			>
				<button className={modal.close} onClick={closeWithReset} type='button'>
					<StackIcon icon='close' stroke='currentcolor' width={14}/>
				</button>

				<form
					className={css.form}
					onSubmit={async event => {
						event.preventDefault()

						if (value?.persona_id) {
							try {
								await send(filters, value.persona_id)
								notificationUtils.snackById('n3.38')
								unSelectAll()
								closeWithReset()
							} catch (error) {
								notifyError(error)
							}
						}
					}}
				>
					<h2 className={css.title}>
						<label htmlFor={id}>Send contacts to team member</label>
					</h2>

					<p
						className={css.lead}
					>{`Select the team member to send ${amount} contacts`}</p>

					<ListSelect
						formatOptionLabel={option => (
							<span>
								{option.nickname || option.email || option.persona_id}
							</span>
						)}
						getOptionValue={option => option.persona_id ?? ''}
						getTag={option => option.nickname || option.email || 'Unknown'}
						inputId={id}
						isLoading={personasQuery.isLoading}
						onChange={member => {
							setValue(member)
						}}
						options={teamMembers}
						placeholder='Select team member'
						value={value}
					/>

					<LoadingButton
						disabled={value === null || !value.persona_id}
						isLoading={isLoading}
						type='submit'
					>
						Send
					</LoadingButton>
				</form>
			</ControlledDialog>
		</>
	)
}